import React, { useEffect, useMemo, useState } from "react";
import { useParams, Link, useSearchParams } from "react-router-dom";
import { Card, CardContent } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { toast } from "sonner";
import { einviteService } from "@/services/einvite.service";
import { rsvpService } from "@/services/rsvp.service";

// Templates
import GardenArchTemplate from "@/components/modules/einvite-templates/GardenArchTemplate";
import PalaceCourtyardTemplate from "@/components/modules/einvite-templates/PalaceCourtyardTemplate";
import WatercolorRosesTemplate from "@/components/modules/einvite-templates/WatercolorRosesTemplate";
import LanternPavilionTemplate from "@/components/modules/einvite-templates/LanternPavilionTemplate";
import MinimalistPinkTemplate from "@/components/modules/einvite-templates/MinimalistPinkTemplate";
import VineyardSunsetTemplate from "@/components/modules/einvite-templates/VineyardSunsetTemplate";
import EnchantedNightArchwayTemplate from "@/components/modules/einvite-templates/EnchantedNightArchwayTemplate";
import TropicalFlowerArchTemplate from "@/components/modules/einvite-templates/TropicalFlowerArchTemplate";
import PalaceArchwayViewTemplate from "@/components/modules/einvite-templates/PalaceArchwayViewTemplate";
import MarigoldGarlandTemplate from "@/components/modules/einvite-templates/MarigoldGarlandTemplate";
import BeachCanopyTemplate from "@/components/modules/einvite-templates/BeachCanopyTemplate";
import StarryNightSkyTemplate from "@/components/modules/einvite-templates/StarryNightSkyTemplate";
import GardenGateChandelierTemplate from "@/components/modules/einvite-templates/GardenGateChandelierTemplate";
import KeralaHouseboatTemplate from "@/components/modules/einvite-templates/KeralaHouseboatTemplate";
import PalaceBalconyTemplate from "@/components/modules/einvite-templates/PalaceBalconyTemplate";
import WatercolorAbstractTemplate from "@/components/modules/einvite-templates/WatercolorAbstractTemplate";
import BeachWeddingArchTemplate from "@/components/modules/einvite-templates/BeachWeddingArchTemplate";
import StarryVineyardNightTemplate from "@/components/modules/einvite-templates/StarryVineyardNightTemplate";

const TEMPLATE_COMPONENTS = {
  "garden-arch": GardenArchTemplate,
  "palace-courtyard": PalaceCourtyardTemplate,
  "watercolor-roses": WatercolorRosesTemplate,
  "lantern-pavilion": LanternPavilionTemplate,
  "minimalist-pink": MinimalistPinkTemplate,
  "vineyard-sunset": VineyardSunsetTemplate,
  "enchanted-night-archway": EnchantedNightArchwayTemplate,
  "tropical-flower-arch": TropicalFlowerArchTemplate,
  "palace-archway-view": PalaceArchwayViewTemplate,
  "marigold-garland": MarigoldGarlandTemplate,
  "beach-canopy": BeachCanopyTemplate,
  "starry-night-sky": StarryNightSkyTemplate,
  "garden-gate-chandelier": GardenGateChandelierTemplate,
  "kerala-houseboat": KeralaHouseboatTemplate,
  "palace-balcony": PalaceBalconyTemplate,
  "watercolor-abstract": WatercolorAbstractTemplate,
  "beach-wedding-arch": BeachWeddingArchTemplate,
  "starry-vineyard-night": StarryVineyardNightTemplate,
};

function VideoInviteView({ videoUrl }) {
  return (
    <div className="w-full max-w-[520px]">
      <div className="rounded-xl overflow-hidden border bg-card">
        <div className="p-3 text-sm font-medium">Video Invite</div>
        <video
          controls
          playsInline
          className="w-full h-auto bg-black"
          src={videoUrl}
        />
      </div>
    </div>
  );
}

function ImageInviteView({ imageUrl }) {
  return (
    <div className="w-full max-w-[520px]">
      <div className="rounded-xl overflow-hidden border bg-card">
        <div className="p-3 text-sm font-medium">Image Invite</div>
        <img src={imageUrl} alt="Invitation" className="w-full h-auto object-contain" />
      </div>
    </div>
  );
}

export default function PublicInvite() {
  const { shareToken } = useParams();
  const [searchParams] = useSearchParams();
  const rsvpToken = searchParams.get("rsvp");

  const [loading, setLoading] = useState(true);
  const [invite, setInvite] = useState(null);
  const [error, setError] = useState("");
  const [rsvpSubmitting, setRsvpSubmitting] = useState(false);

  useEffect(() => {
    const load = async () => {
      if (!shareToken) return;
      try {
        setLoading(true);
        setError("");
        const data = await einviteService.getInviteByToken(shareToken);
        setInvite(data);
      } catch (e) {
        const msg = e?.response?.data?.detail || e?.message || "Invite not found";
        setError(msg);
      } finally {
        setLoading(false);
      }
    };
    load();
  }, [shareToken]);

  const Template = useMemo(() => {
    const templateId = invite?.template_id;
    return TEMPLATE_COMPONENTS[templateId] || GardenArchTemplate;
  }, [invite?.template_id]);

  if (loading) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-lg">
          <CardContent className="p-6 text-center text-muted-foreground">
            Loading invitation…
          </CardContent>
        </Card>
      </div>
    );
  }

  if (error) {
    return (
      <div className="min-h-screen bg-background flex items-center justify-center p-6">
        <Card className="w-full max-w-lg">
          <CardContent className="p-6 space-y-3 text-center">
            <div className="text-lg font-semibold">Invitation not found</div>
            <div className="text-sm text-muted-foreground">{error}</div>
            <div className="flex justify-center gap-2 pt-2">
              <Button variant="outline" onClick={() => window.location.reload()}>
                Retry
              </Button>
              <Button asChild>
                <Link to="/login">Login</Link>
              </Button>
            </div>
          </CardContent>
        </Card>
      </div>
    );
  }

  const inviteData = invite?.invite_data || {};
  const mediaType = inviteData?.mediaType || inviteData?.videoType || inviteData?.imageType || "";
  const isVideoInvite = invite?.template_id === "video-invite" || mediaType === "video";
  const isImageInvite = mediaType === "image";
  const videoUrl = inviteData?.videoUrl || inviteData?.video_url || inviteData?.mediaUrl || "";
  const imageUrl = inviteData?.imageUrl || inviteData?.image_url || inviteData?.mediaUrl || "";

  return (
    <div className="min-h-screen bg-background">
      <div className="max-w-4xl mx-auto p-4 sm:p-6">
        <div className="flex items-center justify-between mb-4">
          <div className="text-sm text-muted-foreground">
            {invite?.wedding?.bride_name && invite?.wedding?.groom_name
              ? `${invite.wedding.bride_name} & ${invite.wedding.groom_name}`
              : "Wedding Invitation"}
          </div>
          <Button
            variant="outline"
            size="sm"
            onClick={() => {
              navigator.clipboard.writeText(window.location.href);
              toast.success("Link copied!");
            }}
          >
            Copy Link
          </Button>
        </div>

        <div className="w-full flex justify-center">
          {isVideoInvite ? (
            <VideoInviteView videoUrl={videoUrl} />
          ) : isImageInvite ? (
            <ImageInviteView imageUrl={imageUrl} />
          ) : (
            // Read-only render: block pointer events so guests can't edit/drag
            <div className="w-full max-w-[520px] pointer-events-none select-none">
              <Template data={inviteData} isDragging={null} />
            </div>
          )}
        </div>

        {/* RSVP actions (only shown when opened via a guest-specific RSVP token) */}
        {rsvpToken && (
          <div className="mt-6 flex justify-center">
            <Card className="w-full max-w-[520px]">
              <CardContent className="p-4 flex flex-col gap-3">
                <div className="text-sm text-muted-foreground">
                  RSVP (confirm your attendance)
                </div>
                <div className="flex gap-2">
                  <Button
                    className="flex-1"
                    disabled={rsvpSubmitting}
                    onClick={async () => {
                      try {
                        setRsvpSubmitting(true);
                        await rsvpService.confirm({ token: rsvpToken, status: "confirmed" });
                        toast.success("RSVP confirmed. Thank you!");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || e?.message || "Failed to confirm RSVP");
                      } finally {
                        setRsvpSubmitting(false);
                      }
                    }}
                  >
                    Confirm
                  </Button>
                  <Button
                    variant="outline"
                    className="flex-1"
                    disabled={rsvpSubmitting}
                    onClick={async () => {
                      try {
                        setRsvpSubmitting(true);
                        await rsvpService.confirm({ token: rsvpToken, status: "declined" });
                        toast.success("RSVP updated. Thank you!");
                      } catch (e) {
                        toast.error(e?.response?.data?.detail || e?.message || "Failed to update RSVP");
                      } finally {
                        setRsvpSubmitting(false);
                      }
                    }}
                  >
                    Decline
                  </Button>
                </div>
                <Button asChild variant="secondary">
                  <Link to={`/rsvp/${rsvpToken}`}>Edit RSVP (guest count / meal)</Link>
                </Button>
              </CardContent>
            </Card>
          </div>
        )}
      </div>
    </div>
  );
}


